import { Skeleton } from '@plato/ui-legacy/components/ui/skeleton';
import { cn } from '@plato/ui/util/classes';
import type React from 'react';
import type { PropsWithChildren } from 'react';

type DataStatBlockProps = {
    children?: React.ReactNode | React.ReactNode[];
    className?: string;
};

export function DataStatBlock({ children, className }: DataStatBlockProps) {
    return <div className={cn('flex flex-col gap-3 md:flex-row', className)}>{children}</div>;
}

type ItemProps = {
    className?: string;
    onClick?: () => void;
} & PropsWithChildren;

function Item({ children, className = '', onClick }: ItemProps) {
    return (
        <div
            className={cn(
                'relative flex h-full flex-col justify-start gap-y-2 rounded-md border border-neutral-200 p-3',
                className,
            )}
            onClick={onClick}
        >
            {children}
        </div>
    );
}

type NoBorderProps = {
    className?: string;
} & PropsWithChildren;

function NoBorder({ children, className = '' }: NoBorderProps) {
    return <div className={cn('flex flex-col space-y-0.5', className)}>{children}</div>;
}

type TitleProps = {
    id?: string;
    className?: string;
} & PropsWithChildren;

function Title({ children, className, id }: TitleProps) {
    return (
        <div id={id} className={cn('pb-0.5 text-sm font-normal text-neutral-700', className)}>
            {children}
        </div>
    );
}

type SubTitleProps = {
    className?: string;
} & PropsWithChildren;

function SubTitle({ children, className }: SubTitleProps) {
    return <div className={cn('text-sm font-medium text-neutral-500', className)}>{children}</div>;
}

type BodyProps = {
    className?: string;
    isLoading?: boolean;
} & PropsWithChildren;

function Body({ children, className, isLoading }: BodyProps) {
    return (
        <div className={cn('text-base flex font-semibold text-neutral-800 items-center', className)}>
            {isLoading ? <Skeleton className="h-6 w-16 rounded" /> : children}
        </div>
    );
}

type BodyRowProps = {
    className?: string;
    isLoading?: boolean;
} & PropsWithChildren;

function BodyRow({ children, className }: BodyRowProps) {
    return <div className={cn('flex flex-row items-center gap-3', className)}>{children}</div>;
}

type FooterProps = {
    className?: string;
    isLoading?: boolean;
} & PropsWithChildren;

function Footer({ children, className, isLoading }: FooterProps) {
    return (
        <div className={cn('text-sm font-normal text-neutral-700 ', className)}>
            {isLoading ? <Skeleton className="h-5 w-24 rounded" /> : children}
        </div>
    );
}

DataStatBlock.Item = Item;
DataStatBlock.NoBorder = NoBorder;
DataStatBlock.Title = Title;
DataStatBlock.SubTitle = SubTitle;
DataStatBlock.Body = Body;
DataStatBlock.BodyRow = BodyRow;
DataStatBlock.Footer = Footer;
